import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, PawPrint, Search, Stethoscope } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { getRecentVets, type RecentVet } from "@/lib/recentlyViewedVets";

interface Pet {
  id: string;
  name: string;
  species: string | null;
}

interface UpcomingAppt {
  id: string;
  date: string;
  time: string | null;
  status: string;
  practice_id: string;
  practice_name?: string | null;
  practice_slug?: string | null;
}

/**
 * Personalized hero shown to signed-in users on the homepage in place of the
 * generic marketing hero. Surfaces pets, upcoming appointments and recently
 * viewed vets so returning users can take action in one click. Returns null
 * (lets the generic hero render) when the user has no usable history.
 */
const PersonalizedHomeHero = () => {
  const { user } = useAuth();
  const [pets, setPets] = useState<Pet[]>([]);
  const [appts, setAppts] = useState<UpcomingAppt[]>([]);
  const [recent, setRecent] = useState<RecentVet[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setRecent(getRecentVets());
  }, []);

  useEffect(() => {
    if (!user) {
      setLoaded(true);
      return;
    }
    let cancelled = false;
    (async () => {
      const today = new Date().toISOString().slice(0, 10);
      const [petsRes, apptsRes] = await Promise.all([
        supabase.from("pets").select("id, name, species").eq("user_id", user.id).is("deleted_at", null).limit(6),
        supabase
          .from("appointments")
          .select("id, date, time, status, practice_id")
          .eq("pet_owner_id", user.id)
          .gte("date", today)
          .in("status", ["pending", "confirmed", "scheduled", "accepted"])
          .order("date", { ascending: true })
          .limit(3),
      ]);
      if (cancelled) return;
      setPets((petsRes.data ?? []) as Pet[]);
      const apptRows = (apptsRes.data ?? []) as UpcomingAppt[];
      if (apptRows.length) {
        const ids = Array.from(new Set(apptRows.map((a) => a.practice_id)));
        const { data: practices } = await supabase
          .from("vet_practices_public")
          .select("id, name, slug")
          .in("id", ids);
        const byId = new Map((practices ?? []).map((p) => [p.id as string, p as { name: string | null; slug: string | null }]));
        for (const a of apptRows) {
          const p = byId.get(a.practice_id);
          a.practice_name = p?.name ?? null;
          a.practice_slug = p?.slug ?? null;
        }
      }
      setAppts(apptRows);
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (!user || !loaded) return null;
  if (pets.length === 0 && appts.length === 0 && recent.length === 0) return null;

  const greeting = (() => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  })();
  const firstName =
    (user.user_metadata?.first_name as string | undefined) ||
    (user.user_metadata?.name as string | undefined)?.split(" ")[0] ||
    user.email?.split("@")[0];

  return (
    <section
      aria-label="Your GetPetVet"
      className="bg-gradient-to-b from-primary/10 via-background to-background border-b border-border"
    >
      <div className="container mx-auto px-4 py-8 sm:py-10 max-w-6xl">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
          <div>
            <p className="text-2xl sm:text-3xl font-semibold tracking-tight text-foreground">
              {greeting}{firstName ? `, ${firstName}` : ""}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              Pick up where you left off, or find a new vet.
            </p>
          </div>
          <Button asChild variant="outline" size="sm">
            <Link to="/find-vets"><Search className="w-4 h-4 mr-1.5" /> Search vets</Link>
          </Button>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          {/* Upcoming appointments */}
          <div className="rounded-xl border border-border bg-card p-4 flex flex-col">
            <div className="flex items-center gap-2 mb-2 text-sm font-medium text-foreground">
              <CalendarClock className="w-4 h-4 text-primary" /> Upcoming
            </div>
            {appts.length === 0 ? (
              <p className="text-xs text-muted-foreground flex-1">No upcoming visits booked.</p>
            ) : (
              <ul className="space-y-2 flex-1">
                {appts.map((a) => (
                  <li key={a.id} className="text-xs">
                    <Link
                      to="/my-dashboard"
                      className="block rounded-md border border-border bg-background px-2.5 py-2 hover:border-primary transition-colors"
                    >
                      <span className="font-medium text-foreground">
                        {a.practice_name ?? "Your vet"}
                      </span>
                      <span className="block text-muted-foreground">
                        {new Date(a.date).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
                        {a.time ? ` · ${a.time}` : ""}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            <Link to="/my-dashboard" className="text-xs text-primary hover:underline mt-3 self-start">
              View dashboard →
            </Link>
          </div>

          {/* Your pets */}
          <div className="rounded-xl border border-border bg-card p-4 flex flex-col">
            <div className="flex items-center gap-2 mb-2 text-sm font-medium text-foreground">
              <PawPrint className="w-4 h-4 text-primary" /> Your pets
            </div>
            {pets.length === 0 ? (
              <p className="text-xs text-muted-foreground flex-1">No pets yet.</p>
            ) : (
              <ul className="flex flex-wrap gap-1.5 flex-1 content-start">
                {pets.map((p) => (
                  <li key={p.id}>
                    <Link
                      to={`/pet/${p.id}`}
                      className="inline-flex items-center rounded-full border border-border bg-background px-2.5 py-1 text-xs hover:border-primary hover:bg-primary/5 transition-colors"
                    >
                      {p.name}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            <Link to="/add-pet" className="text-xs text-primary hover:underline mt-3 self-start">
              Add a pet →
            </Link>
          </div>

          {/* Book again / recent vets */}
          <div className="rounded-xl border border-border bg-card p-4 flex flex-col">
            <div className="flex items-center gap-2 mb-2 text-sm font-medium text-foreground">
              <Stethoscope className="w-4 h-4 text-primary" /> Book again
            </div>
            {recent.length === 0 ? (
              <p className="text-xs text-muted-foreground flex-1">
                Vets you visit will appear here for one-tap rebooking.
              </p>
            ) : (
              <ul className="space-y-1.5 flex-1">
                {recent.slice(0, 3).map((v) => (
                  <li key={v.id}>
                    <Link
                      to={`/vet/${v.slug || v.id}`}
                      className="block rounded-md border border-border bg-background px-2.5 py-2 text-xs hover:border-primary transition-colors"
                    >
                      <span className="font-medium text-foreground">{v.name}</span>
                      {v.postcode && (
                        <span className="block text-muted-foreground">{v.postcode}</span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PersonalizedHomeHero;
